import React from 'react';
import { CheckCircle, Sparkles, X, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

const severityBadgeClass = (severity) => {
  if (severity === 'CRITICO') return 'bg-red-100 text-red-700 border-red-300';
  if (severity === 'ALTO') return 'bg-orange-100 text-orange-700 border-orange-300';
  if (severity === 'MEDIO') return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-green-100 text-green-700 border-green-300';
};

export const AISuggestionsBanner = ({ aiSuggestions, approvingAI, onApprove }) => {
  if (!aiSuggestions) return null;

  if (aiSuggestions.status === 'PENDENTE_APROVACAO') {
    return (
      <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm" data-testid="ai-suggestions-banner">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-amber-900">Sugestões da IA Pendentes de Aprovação</h3>
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                  Aguarda Revisão
                </Badge>
              </div>
              <p className="text-sm text-amber-800 mb-3">
                A análise automática sugere as seguintes classificações. Reveja e aprove ou rejeite antes de prosseguir com o workflow.
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-4">
                <div className="bg-white rounded-lg px-4 py-2 border border-amber-200">
                  <p className="text-xs text-slate-500">Categoria Sugerida</p>
                  <p className="font-medium text-slate-900">{aiSuggestions.suggested_category || 'N/A'}</p>
                </div>
                <div className="bg-white rounded-lg px-4 py-2 border border-amber-200">
                  <p className="text-xs text-slate-500">Gravidade Sugerida</p>
                  <Badge variant="outline" className={severityBadgeClass(aiSuggestions.suggested_severity)}>
                    {aiSuggestions.suggested_severity || 'N/A'}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={() => onApprove('APROVAR')}
                  disabled={approvingAI}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="approve-ai-suggestions-btn"
                >
                  {approvingAI ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Aprovar e Aplicar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onApprove('REJEITAR')}
                  disabled={approvingAI}
                  data-testid="reject-ai-suggestions-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Rejeitar
                </Button>
                <span className="text-xs text-slate-500 ml-2">
                  Analisado em {aiSuggestions.analyzed_at ? new Date(aiSuggestions.analyzed_at).toLocaleString('pt-PT') : ''}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Alert className={aiSuggestions.status === 'APROVADO' ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}>
      <Sparkles className={`w-4 h-4 ${aiSuggestions.status === 'APROVADO' ? 'text-emerald-600' : 'text-slate-500'}`} />
      <AlertDescription className={aiSuggestions.status === 'APROVADO' ? 'text-emerald-700' : 'text-slate-600'}>
        {aiSuggestions.status === 'APROVADO'
          ? 'Sugestões IA aprovadas e aplicadas'
          : 'Sugestões IA rejeitadas - classificação manual necessária'}
      </AlertDescription>
    </Alert>
  );
};
